import { toast } from "@assetwatch/ui";
import type { WorkRoleCode } from "@assetwatch/work-authorization";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { workClient } from "../clients/workClient";
import { useWorkScope } from "../components/WorkScopeProvider";

export type { WorkRoleCode } from "@assetwatch/work-authorization";

async function mutationError(response: Response, fallback: string) {
  const body = (await response.json().catch(() => null)) as {
    error?: unknown;
  } | null;
  return typeof body?.error === "string" ? body.error : fallback;
}

export function useTeamsUsersMutations() {
  const queryClient = useQueryClient();
  const { selectedCustomer, selectedFacility } = useWorkScope();
  const invalidate = () =>
    queryClient.invalidateQueries({
      queryKey: ["teams-users", selectedCustomer?.id, selectedFacility?.id],
    });
  function context() {
    if (!selectedCustomer || !selectedFacility)
      throw new Error("A customer and facility must be selected.");
    return { customerId: selectedCustomer.id, facilityId: selectedFacility.id };
  }
  const createTeam = useMutation({
    mutationFn: async (input: { name: string; description?: string }) => {
      const response = await workClient.v1.customers[":customerId"].facilities[
        ":facilityId"
      ].teams.$post({
        param: context(),
        json: { name: input.name, description: input.description },
      });
      if (!response.ok)
        throw new Error(
          await mutationError(response, "Unable to create team."),
        );
    },
    onSuccess: async () => {
      await invalidate();
      toast.add("Team created.", { icon: true });
    },
  });
  const replaceTeam = useMutation({
    mutationFn: async (input: {
      teamId: number;
      name: string;
      description?: string;
      isActive: boolean;
      members: { userId: number; isTeamLead: boolean }[];
    }) => {
      const response = await workClient.v1.customers[":customerId"].facilities[
        ":facilityId"
      ].teams[":teamId"].$put({
        param: { ...context(), teamId: String(input.teamId) },
        json: {
          name: input.name,
          description: input.description,
          isActive: input.isActive,
          members: input.members,
        },
      });
      if (!response.ok)
        throw new Error(
          await mutationError(response, "Unable to save the team."),
        );
    },
    onSuccess: async () => {
      await invalidate();
      toast.add("Team updated.", { icon: true });
    },
  });
  const setUserRole = useMutation({
    mutationFn: async (input: { userId: number; role: WorkRoleCode }) => {
      const response = await workClient.v1.customers[":customerId"].facilities[
        ":facilityId"
      ].users[":userId"].role.$patch({
        param: { ...context(), userId: String(input.userId) },
        json: { role: input.role },
      });
      if (!response.ok)
        throw new Error(
          await mutationError(response, "Unable to update the user's role."),
        );
    },
    onSuccess: async () => {
      await invalidate();
      toast.add("Role updated.", { icon: true });
    },
    onError: (error) => {
      toast.error(
        error instanceof Error
          ? error.message
          : "Unable to update the user's role. Try again.",
        { icon: true },
      );
    },
  });
  return { createTeam, replaceTeam, setUserRole };
}
